/**
 * Copyright © 2026 Adnuntius AS.
 */
import {CompactType, DisplayGrid, GridsterConfig, GridsterItemConfig, GridType} from 'angular-gridster2';

export interface AdnGridsterLegacyOptions {
  columns?: number;
  defaultSizeX?: number;
  defaultSizeY?: number;
  emptyMinRows?: number;
  rowHeight?: number;
  minRows?: number;
  margins?: number[];
  mobileModeEnabled?: boolean;
  swapItems?: boolean;
  draggable?: {
    enabled?: boolean;
    handle?: string;
    stop?: () => void;
  };
  resizable?: {
    enabled?: boolean;
    stop?: () => void;
  };
}

export interface AdnGridsterItem extends GridsterItemConfig {
  data: any;
}

export const ADN_GRIDSTER_CONFIG = {
  WORKSPACES: {
    minRows: 1,
    swapItems: true,
    resizable: {
      enabled: true
    },
    rowHeight: 210,
    draggable: {
      handle: '.dragDropHandle'
    },
    mobileModeEnabled: false,
    margins: [10, 10]
  } as AdnGridsterLegacyOptions,
  RESOURCES: {
    mobileModeEnabled: false,
    columns: 1,
    defaultSizeX: 1,
    defaultSizeY: 2,
    rowHeight: 165,
    minRows: 2,
    swapItems: true,
    resizable: {enabled: false}
  } as AdnGridsterLegacyOptions
};

export function computeGridsterMinRows(
  gridItems: AdnGridsterItem[],
  fallbackMinRows = 1
): number {
  if (!gridItems.length) {
    return fallbackMinRows;
  }
  return Math.max(
    fallbackMinRows,
    ...gridItems.map((item) => (item.y ?? 0) + (item.rows ?? 1))
  );
}

export function hasSavedGridPosition(dataItem: any): boolean {
  const col = dataItem?.col;
  const row = dataItem?.row;
  if (col === undefined || col === null || row === undefined || row === null) {
    return false;
  }
  return col >= 0 && row >= 0;
}

export function normalizeGridsterItemBounds(
  item: AdnGridsterItem,
  maxCols: number
): void {
  item.cols = Math.min(Math.max(item.cols ?? 1, 1), maxCols);
  item.rows = Math.max(item.rows ?? 1, 1);
  if (item.x !== undefined && item.x >= 0) {
    item.x = Math.max(item.x, 0);
    if (item.x + item.cols > maxCols) {
      item.x = 0;
    }
  }
  if (item.y !== undefined && item.y >= 0) {
    item.y = Math.max(item.y, 0);
  }
}

export function compactGridItemsUp(
  gridItems: AdnGridsterItem[]
): boolean {
  let changed = false;
  const sortedItems = [...gridItems].sort((left, right) => {
    const rowDiff = (left.y ?? 0) - (right.y ?? 0);
    return rowDiff !== 0 ? rowDiff : (left.x ?? 0) - (right.x ?? 0);
  });

  sortedItems.forEach((item) => {
    let targetRow = item.y ?? 0;
    while (targetRow > 0) {
      const nextRow = targetRow - 1;
      const blocked = gridItems.some((other) => {
        if (other === item) {
          return false;
        }
        return gridItemsOverlap(
          item.x ?? 0, nextRow, item.cols ?? 1, item.rows ?? 1,
          other.x ?? 0, other.y ?? 0, other.cols ?? 1, other.rows ?? 1
        );
      });
      if (blocked) {
        break;
      }
      targetRow = nextRow;
    }
    if (targetRow !== item.y) {
      item.y = targetRow;
      syncLegacyGridsterFields(item);
      changed = true;
    }
  });

  return changed;
}

function gridItemsOverlap(
  leftX: number,
  leftY: number,
  leftCols: number,
  leftRows: number,
  rightX: number,
  rightY: number,
  rightCols: number,
  rightRows: number
): boolean {
  return leftX < rightX + rightCols
    && leftX + leftCols > rightX
    && leftY < rightY + rightRows
    && leftY + leftRows > rightY;
}

export function buildGridster2Options(
  legacyOptions: AdnGridsterLegacyOptions = {},
  onLayoutChange?: () => void
): GridsterConfig {
  const margin = legacyOptions.margins?.[0] ?? 10;
  const columns = legacyOptions.columns ?? 6;
  const draggableEnabled = legacyOptions.draggable?.enabled !== false;
  const resizableEnabled = legacyOptions.resizable?.enabled !== false;
  const dragHandleClass = legacyOptions.draggable?.handle?.replace(/^\./, '') || 'dragDropHandle';

  const notifyLayoutChange = (): void => {
    legacyOptions.draggable?.stop?.();
    legacyOptions.resizable?.stop?.();
    onLayoutChange?.();
  };

  const useSwap = legacyOptions.swapItems === true;

  const options: GridsterConfig = {
    gridType: GridType.VerticalFixed,
    setGridSize: true,
    compactType: CompactType.None,
    useTransformPositioning: true,
    minCols: columns,
    maxCols: columns,
    minRows: legacyOptions.minRows ?? 1,
    fixedRowHeight: legacyOptions.rowHeight ?? 210,
    margin,
    outerMargin: true,
    mobileBreakpoint: legacyOptions.mobileModeEnabled === false ? 0 : 640,
    defaultItemCols: legacyOptions.defaultSizeX ?? 1,
    defaultItemRows: Math.max(legacyOptions.defaultSizeY ?? 1, 1),
    draggable: {
      enabled: draggableEnabled,
      dragHandleClass,
      ignoreContent: true,
      stop: notifyLayoutChange
    },
    resizable: {
      enabled: resizableEnabled,
      stop: notifyLayoutChange
    },
    swap: useSwap,
    swapWhileDragging: false,
    pushItems: !useSwap,
    disablePushOnDrag: useSwap,
    disableScrollHorizontal: true,
    disableScrollVertical: true,
    // scrollIntoView during layout can resize→recalculate→markForCheck (NG0103).
    scrollToNewItems: false,
    disableWarnings: true,
    displayGrid: DisplayGrid.None,
    itemChangeCallback: (item) => {
      syncLegacyGridsterFields(item as AdnGridsterItem);
    },
    itemRemovedCallback: () => {
      onLayoutChange?.();
    },
    // angular-gridster2 v21 keeps api on the component; stash it on options so
    // callers can auto-position without holding a ViewChild.
    initCallback: (_gridster, api) => {
      (options as {api?: unknown}).api = api;
    }
  };
  return options;
}

export function createGridsterItem(
  dataItem: any,
  legacyOptions: AdnGridsterLegacyOptions = {},
  maxCols?: number
): AdnGridsterItem {
  const columns = maxCols ?? legacyOptions.columns ?? 6;
  const defaultCols = legacyOptions.defaultSizeX ?? columns;
  const defaultRows = Math.max(legacyOptions.defaultSizeY ?? 1, 1);

  const hasPosition = hasSavedGridPosition(dataItem);
  const item: AdnGridsterItem = {
    data: dataItem,
    cols: dataItem.sizeX ?? defaultCols,
    rows: Math.max(dataItem.sizeY ?? defaultRows, 1),
    x: hasPosition ? (dataItem.col ?? dataItem.x ?? 0) : -1,
    y: hasPosition ? (dataItem.row ?? dataItem.y ?? 0) : -1
  };

  normalizeGridsterItemBounds(item, columns);
  syncLegacyGridsterFields(item);
  return item;
}

export function syncLegacyGridsterFields(item: AdnGridsterItem): void {
  if (!item?.data) {
    return;
  }
  if (item.cols !== undefined) {
    item.data.sizeX = item.cols;
  }
  if (item.rows !== undefined) {
    item.data.sizeY = item.rows;
  }
  if (item.x !== undefined && item.y !== undefined && item.x >= 0 && item.y >= 0) {
    item.data.col = item.x;
    item.data.row = item.y;
  }
}
